import showdown from 'showdown';
import DOMPurify from 'dompurify';

// Initialize Showdown converter with typography-focused options
const converter = new showdown.Converter({
  flavor: 'github',
  ghCompatibleHeaderId: true,
  simpleLineBreaks: false, // Respect markdown line breaks for proper paragraphs
  requireSpaceBeforeHeadingText: true,
  openLinksInNewWindow: true,
  backslashEscapesHTMLTags: true,
  strikethrough: true,
  tables: true,
  tasklists: true,
  // Proper list handling
  disableForced4SpacesIndentedSublists: false,
  literalMidWordUnderscores: true,
  // Typography improvements
  headerLevelStart: 1,
  smoothLivePreview: true,
  noHeaderId: false
});

export function convertMarkdownToSafeHtml(markdown: string): string {
  if (!markdown || typeof markdown !== 'string' || !markdown.trim()) return '';
  try {
    const rawHtml = converter.makeHtml(markdown);
    
    // Sanitize the HTML to prevent XSS attacks
    let safeHtml = DOMPurify.sanitize(rawHtml, {
      USE_PROFILES: { html: true }, // Ensure basic HTML elements are allowed
      // FORBID_TAGS: ['style'], // Example: forbid style tags if necessary
      // FORBID_ATTR: ['onerror'] // Example: forbid onerror attributes
    });

    // Post-processing: Clean up common issues from Markdown conversion
    // 1. Remove empty heading tags (e.g., <h1></h1>, <h2> </h2>)
    safeHtml = safeHtml.replace(/<h[1-6]>\s*<\/h[1-6]>/gi, '');
    // 2. Remove empty paragraph tags (e.g. <p></p> or <p> </p> or <p><br></p>)
    safeHtml = safeHtml.replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, '');
    // 3. Collapse multiple consecutive <br> tags into one (or two for a paragraph-like break)
    safeHtml = safeHtml.replace(/(<br\s*\/?>\s*){2,}/gi, '<br/><br/>'); // Max two for paragraph-like break
    // 4. Trim leading/trailing <br> tags from the whole string if they exist
    safeHtml = safeHtml.replace(/^(\s*<br\s*\/?>)+|(<br\s*\/?>\s*)+$/gi, '');
    // 5. Remove paragraphs that only contain empty space or <br> tags after other cleanups
    safeHtml = safeHtml.replace(/<p>(\s*|<br\s*\/?>)*<\/p>/gi, '');
    
    // 6. Minimal cleanup while respecting semantic structure and typography
    // Only merge consecutive lists of the same type (proper typography)
    safeHtml = safeHtml.replace(/<\/ol>\s*<ol>/gi, '');
    safeHtml = safeHtml.replace(/<\/ul>\s*<ul>/gi, '');
    
    // Clean up redundant numbering/bullets inside list items (common markdown conversion issue)
    safeHtml = safeHtml.replace(/<li>\s*\d+\.\s+(.*?)<\/li>/gi, '<li>$1</li>');
    safeHtml = safeHtml.replace(/<li>\s*[•\-\*]\s+(.*?)<\/li>/gi, '<li>$1</li>');

    return safeHtml.trim();
  } catch (error) {
    console.error('[convertMarkdownToSafeHtml] Error converting markdown:', error);
    return DOMPurify.sanitize(markdown.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'));
  }
}

export function formatLLMResponseToHtml(text: string, codeBlocks: any[] = [], language: string | null = null): string {
  if (!text) return '';

  try {
    let formattedText = text;

    const codeBlockPlaceholders = new Map<string, string>();
    // First, handle passed codeBlocks (if any)
    if (codeBlocks && codeBlocks.length > 0) {
      codeBlocks.forEach((block, index) => {
        const placeholder = `__CODE_BLOCK_PLACEHOLDER_${index}__`;
        const code = block.code || '';
        const lang = block.language || language || '';
        const escapedCode = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        codeBlockPlaceholders.set(placeholder, `<pre><code class="language-${lang}">${escapedCode}</code></pre>`);
        // This replace assumes block.code is unique in the text. A more robust placeholder system might be needed.
        formattedText = formattedText.replace(block.code, placeholder);
      });
    } else {
      // If no codeBlocks prop, try to identify markdown-style code blocks
      // Multi-line code blocks
      formattedText = formattedText.replace(/```(\w*)\n([\s\S]*?)\n```/g, (_, lang, code) => {
        const placeholder = `__CODE_BLOCK_PLACEHOLDER_MD_${codeBlockPlaceholders.size}__`;
        const escapedCode = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        codeBlockPlaceholders.set(placeholder, `<pre><code class="language-${lang || ''}">${escapedCode}</code></pre>`);
        return placeholder;
      });
      // Inline code
      formattedText = formattedText.replace(/`([^`]+)`/g, (_, code) => {
        const placeholder = `__INLINE_CODE_PLACEHOLDER_${codeBlockPlaceholders.size}__`;
        const escapedCode = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        codeBlockPlaceholders.set(placeholder, `<code>${escapedCode}</code>`);
        return placeholder;
      });
    }

    // Basic HTML escaping for the rest of the text (text that is NOT a code block)
    // This should happen AFTER markdown to HTML conversion for text content, but BEFORE re-inserting code blocks.
    // For simplicity, we're doing a broad escape here, then specific markdown conversions.
    // A more sophisticated approach would parse markdown, then escape text nodes only.
    formattedText = formattedText
      .replace(/&/g, '&amp;') // Escape ampersands first
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Convert markdown-style formatting to HTML
    // Headers (must be at the beginning of a line)
    // Process from most specific (######) to least specific (#)
    formattedText = formattedText
      .replace(/^######\s+(.*)/gm, '<h6>$1</h6>')
      .replace(/^#####\s+(.*)/gm, '<h5>$1</h5>')
      .replace(/^####\s+(.*)/gm, '<h4>$1</h4>')
      .replace(/^###\s+(.*)/gm, '<h3>$1</h3>')
      .replace(/^##\s+(.*)/gm, '<h2>$1</h2>')
      .replace(/^#\s+(.*)/gm, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Strikethrough
      .replace(/~~(.*?)~~/g, '<del>$1</del>')
      // Unordered lists
      .replace(/^\*\s+(.*)/gm, '<li>$1</li>')
      .replace(/^-\s+(.*)/gm, '<li>$1</li>')
      .replace(/^\+\s+(.*)/gm, '<li>$1</li>')
      // Ordered lists
      .replace(/^\d+\.\s+(.*)/gm, '<li>$1</li>');

    // Wrap consecutive <li> items in <ul> or <ol>
    // This is a simplified approach and might not cover all edge cases perfectly.
    formattedText = formattedText.replace(/^(<li>.*?<\/li>\s*)+/gm, (match) => {
      if (match.startsWith('<li>') && /<li>\d+\./.test(match) === false) { // Rough check for unordered
         // Check if already wrapped, if so, just return match to avoid double wrapping
        if (match.startsWith('<ul>') && match.endsWith('</ul>')) return match;
        return `<ul>\n${match.trim()}\n</ul>`;
      }
      return match; // Return as is if not matching expected <li> sequence for ul
    });
     formattedText = formattedText.replace(/^(<li>.*?<\/li>\s*)+/gm, (match) => {
      if (match.startsWith('<li>')) { // Rough check for ordered or previously missed unordered
        // Check if already wrapped, if so, just return match to avoid double wrapping
        if (match.startsWith('<ol>') && match.endsWith('</ol>')) return match;
        if (match.startsWith('<ul>') && match.endsWith('</ul>')) return match; // Avoid re-wrapping what might have been UL
        // If it looks like an ordered list item (e.g. starts with number. which was converted to <li>)
        // or it's a generic list item not yet wrapped by UL, wrap with OL as a general list wrapper.
        // This part is heuristic.
        return `<ol>\n${match.trim()}\n</ol>`;
      }
      return match;
    });

    // Citation markers (after other processing, ensure they are not mangled by HTML escaping if they contain < or >)
    formattedText = formattedText.replace(/\[(\d+)\]/g, (match) => {
         // Re-escape < and > if the placeholder itself was directly matched, instead of its content
        const safeMatch = match.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
      return `<span class="citation-marker-placeholder">${safeMatch}</span>`;
    });

    // Paragraphs and line breaks
    // Split by newlines, then wrap non-empty lines/blocks in <p>
    // This is a more robust way to handle paragraphs than just replacing \n\n
    const lines = formattedText.split(/\n/);
    let inParagraph = false;
    let finalHtml = '';
    for (const line of lines) {
        if (line.trim() === '') {
            if (inParagraph) {
                finalHtml += '</p>\n';
                inParagraph = false;
            }
        } else if (line.match(/^<(h[1-6]|ul|ol|li|pre|blockquote|del|strong|em)/i)) {
             if (inParagraph) {
                finalHtml += '</p>\n';
                inParagraph = false;
            }
            finalHtml += line + '\n';
        } else {
            if (!inParagraph) {
                finalHtml += '<p>';
                inParagraph = true;
            }
            finalHtml += line + (inParagraph ? '<br/>\n' : '\n'); // Add <br/> for line breaks within a paragraph
        }
    }
    if (inParagraph) {
        finalHtml += '</p>\n';
    }
    formattedText = finalHtml.replace(/<br\/>\n<\/p>/g, '</p>'); // Clean up <br/> at end of <p>
    formattedText = formattedText.replace(/<p>\s*<\/p>/g, ''); // Clean up empty paragraphs
    formattedText = formattedText.replace(/<p><br\s*\/?><\/p>/g, ''); // Clean up paragraphs with only a br

    // Restore code blocks
    codeBlockPlaceholders.forEach((html, placeholder) => {
      const placeholderRegExp = new RegExp(placeholder.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&'), 'g');
      formattedText = formattedText.replace(placeholderRegExp, html);
    });

    return formattedText.trim();
  } catch (error) {
    console.error('[formatLLMResponseToHtml] Error formatting text:', error);
    return `<p>${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
  }
}

/**
 * Processes a plain text LLM response into well-structured HTML with citation support
 * 
 * @param plainText - The plain text response from the LLM
 * @param options - Configuration options
 * @returns Fully formatted HTML content with interactive citations
 */
export function processLLMResponse(
  plainText: string, 
  options: {
    citationSources?: any[],
    onCitationClick?: ((index: number) => void) | null,
    applySyntaxHighlight?: boolean
  } = {}
): string {
  const { citationSources = [], onCitationClick = null, applySyntaxHighlight = true } = options;
  
  // Return safe fallback for empty input
  if (!plainText) return '';
  
  // If the content is already HTML, return it directly to avoid processing errors
  if (plainText.startsWith('<') && plainText.includes('</')) {
    return plainText;
  }
  
  try {
    console.log(`[citationHelper] Processing LLM response with ${plainText.length} chars and ${citationSources?.length || 0} sources`);
    
    // First, protect citation markers from markdown processing
    const citationPlaceholders = new Map();
    let protectedText = plainText;
    
    // Replace citation markers with placeholders that won't be affected by markdown
    // Supports both [Source N] format (new) and [N] format (legacy)
    protectedText = protectedText.replace(/\[Source\s*(\d+)\]/gi, (match, num) => {
      const placeholder = `CITATIONMARKER${num}CITATIONMARKER`;
      citationPlaceholders.set(placeholder, match);
      return placeholder;
    });
    // Also handle legacy [N] format for backwards compatibility
    protectedText = protectedText.replace(/\[(\d+)\]/g, (match, num) => {
      const placeholder = `CITATIONMARKER${num}CITATIONMARKER`;
      if (!citationPlaceholders.has(placeholder)) {
        citationPlaceholders.set(placeholder, match);
      }
      return placeholder;
    });
    
    // Use the more reliable markdown converter
    let formattedHtml = convertMarkdownToSafeHtml(protectedText);
    
    // Restore citation markers
    citationPlaceholders.forEach((originalMarker, placeholder) => {
      // Use a more robust replacement that handles HTML entities
      const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      formattedHtml = formattedHtml.replace(new RegExp(escapedPlaceholder, 'g'), originalMarker);
    });
    
    // Convert citation markers to interactive spans/links
    // Supports both [Source N] format (new) and [N] format (legacy)
    // If citationSources is provided, renders as clickable links with source titles
    {
      // Process [Source N] format first
      formattedHtml = formattedHtml.replace(/\[Source\s*(\d+)\]/gi, (_, sourceNumber) => {
        const index = parseInt(sourceNumber, 10) - 1;
        const source = citationSources?.[index];

        // If we have source data, render as a clickable link with title
        if (source && source.url) {
          const title = source.title || `Source ${sourceNumber}`;
          return `<a href="${source.url}" target="_blank" rel="noopener noreferrer" class="citation-link" data-citation-index="${index}" data-source-number="${sourceNumber}">\u2197 ${title}</a>`;
        } else if (source && source.title) {
          // Source exists with title but no URL (e.g., document) - same teal style
          return `<span class="citation-document" data-citation-index="${index}" data-source-number="${sourceNumber}">\u2197 ${source.title}</span>`;
        }
        // Fallback: no source data yet
        return `<span class="citation-marker" data-citation-index="${index}" data-source-number="${sourceNumber}">${sourceNumber}</span>`;
      });

      // Process legacy [N] format for backwards compatibility
      formattedHtml = formattedHtml.replace(/\[(\d+)\]/g, (_, sourceNumber) => {
        const index = parseInt(sourceNumber, 10) - 1;
        const source = citationSources?.[index];

        if (source && source.url) {
          const title = source.title || `Source ${sourceNumber}`;
          return `<a href="${source.url}" target="_blank" rel="noopener noreferrer" class="citation-link" data-citation-index="${index}" data-source-number="${sourceNumber}">\u2197 ${title}</a>`;
        } else if (source && source.title) {
          return `<span class="citation-document" data-citation-index="${index}" data-source-number="${sourceNumber}">\u2197 ${source.title}</span>`;
        }
        return `<span class="citation-marker" data-citation-index="${index}" data-source-number="${sourceNumber}">${sourceNumber}</span>`;
      });
      
      // Add event handlers for citation markers if needed
      if (typeof document !== 'undefined') {
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(formattedHtml, 'text/html');
          
          const markers = doc.querySelectorAll('.citation-marker');
          markers.forEach(marker => {
            const index = parseInt(marker.getAttribute('data-citation-index') || '-1', 10);
            const sourceNumber = parseInt(marker.getAttribute('data-source-number') || '0', 10);
            
            if (!isNaN(index) && !isNaN(sourceNumber)) {
              // Add click handler if provided
              if (onCitationClick) {
                marker.setAttribute('onclick', `arguments[0].stopPropagation(); window.handleCitationClick && window.handleCitationClick(${index})`);
              }
              
              // Add hover event handlers
              marker.setAttribute('onmouseenter', `this.classList.add('active'); window.handleCitationHover && window.handleCitationHover(${index}, true)`);
              marker.setAttribute('onmouseleave', `this.classList.remove('active'); window.handleCitationHover && window.handleCitationHover(${index}, false)`);
            }
          });
          
          formattedHtml = doc.body.innerHTML;
        } catch (domError) {
          console.error('[citationHelper] Error processing DOM for citations:', domError);
          // Continue with the formatted HTML we have so far
        }
      }
    }
    
    // Apply syntax highlighting for code blocks if enabled
    if (applySyntaxHighlight) {
      try {
        // Find all <pre><code> blocks and add syntax highlighting classes
        if (typeof document !== 'undefined') {
          const parser = new DOMParser();
          const doc = parser.parseFromString(formattedHtml, 'text/html');
          
          const codeBlocks = doc.querySelectorAll('pre code');
          codeBlocks.forEach(block => {
            // Add syntax highlighting class if not already present
            if (!block.classList.contains('language-') && !block.classList.contains('hljs')) {
              block.classList.add('language-javascript'); // Default to JS highlighting
            }
          });
          
          formattedHtml = doc.body.innerHTML;
        }
      } catch (syntaxError) {
        console.error('[citationHelper] Error applying syntax highlighting:', syntaxError);
        // Continue with the HTML we have so far
      }
    }
    
    return formattedHtml;
  } catch (error) {
    console.error('[citationHelper] Fatal error in processLLMResponse:', error);
    // Return safe fallback to ensure content is always visible
    return `<p>${plainText.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
  }
} 